import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { channelsBadgeLabel } from '../../lib/campaignChannels';
import { Input } from '../../components/ui/input';
import { MultiSelectFilter } from '../../components/ui/MultiSelectFilter';
import { SelectField } from '../../components/ui/SelectField';
import { Combobox } from '../../components/ui/Combobox';
import { campaignStatusVariant } from './campaignStatusVariant';
import { CenteredSpinner } from '../../components/ui/Spinner';
import {
  ACCOUNT_TYPE_LABEL,
  compareAccountTypeForSummary,
} from '../../lib/accountTypeLabel';
import type { Campaign, CampaignStatus } from '../../types/campaign';
import type { Account } from '../../types/account';
import { listAccounts } from '../accounts/api';
import { listCampaigns } from './api';
import {
  matchesDesignation,
  matchesScope,
  type DesignationFilter,
  type ScopeFilter,
} from './campaignFilters';
import {
  ALL_STATUSES,
  DEFAULT_STATUS_SELECTION,
  readFiltersFromParams,
  statusEqualsDefault,
  writeFiltersToParams,
} from './campaignFilterUrl';

function StatusBadge({ status }: { status: CampaignStatus }) {
  return <Badge variant={campaignStatusVariant(status)}>{status}</Badge>;
}

// Pre-P2b-3 polish (Fix 5): "Assigned to" column summary. Maps each
// shareTarget id to its Account, sorts brand → group → location-tier,
// alphabetical within each tier, and returns:
//   - first label + " + N more" suffix when 2+ targets
//   - just the first label when exactly 1
//   - "(none)" when shareTargets is empty (old-shape Campaign)
//
// `full` is the complete list, surfaced as a hover tooltip so AMs can
// see every target without expanding a row. Unknown accountIds (deleted
// Accounts, stale data) render as a dimmed placeholder so the list
// reads predictably even when the hierarchy is incomplete.
type ShareTargetSummary = { headline: string; full: string[] };

function summariseShareTargets(
  campaign: Campaign,
  byId: Map<string, Account>,
): ShareTargetSummary {
  const targets = campaign.shareTargets ?? [];
  if (targets.length === 0) return { headline: '(none)', full: [] };
  const labelled = targets.map((t) => {
    const acc = byId.get(t.accountId);
    if (!acc) {
      return { type: 'location' as const, name: '(unknown)', label: '(unknown account)' };
    }
    return {
      type: acc.accountType,
      name: acc.name,
      label: `${ACCOUNT_TYPE_LABEL[acc.accountType]}: ${acc.name}`,
    };
  });
  labelled.sort((a, b) => {
    const t = compareAccountTypeForSummary(a.type, b.type);
    return t !== 0 ? t : a.name.localeCompare(b.name);
  });
  const full = labelled.map((l) => l.label);
  const headline =
    labelled.length === 1
      ? labelled[0]!.label
      : `${labelled[0]!.label} + ${labelled.length - 1} more`;
  return { headline, full };
}

function DesignationBadge({ campaign }: { campaign: Campaign }) {
  if (campaign.isAlternate) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
        Alternate
      </span>
    );
  }
  if (campaign.isOptOut) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-700">
        Opt Out
      </span>
    );
  }
  return null;
}

// Pre-P2b-3 polish round 2 (Item 2): filter bar status display labels.
// Match the existing pill labels — no new terminology — so "Inactive"
// reads the same in the filter as it does on the row. The status set
// + default selection are exported from campaignFilterUrl so the URL
// codec and the component agree on the canonical list.
const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
};

const DESIGNATION_LABEL: Record<DesignationFilter, string> = {
  all: 'All designations',
  alternate: 'Alternate',
  opt_out: 'Opt Out',
  regular: 'Regular',
};

const SCOPE_LABEL: Record<ScopeFilter, string> = {
  all: 'All scopes',
  brand: 'Brand-shared',
  group: 'Group-shared',
  location: 'Location-shared',
  multi: 'Multi-shared',
  other: 'Other-type-shared',
};


// Mutually exclusive scoping props:
//   brandAccountId             — scope to brand context (visibility walk).
//                                Hides the Brand column. Used by the
//                                brand-AccountDetail Campaigns section.
//   directlySharedWithAccountId — scope to Campaigns whose shareTargets[]
//                                contain this accountId verbatim (no
//                                walk-up, no walk-down). Used by non-brand
//                                AccountDetail Campaigns sections
//                                (Pre-P2b-3 polish Fix 3). Brand column
//                                stays visible because campaigns shared
//                                with a non-brand account can originate
//                                from any brand.
//   neither                    — global view; all filters available.
export type CampaignListProps = {
  brandAccountId?: string;
  directlySharedWithAccountId?: string;
};

export function CampaignList({
  brandAccountId,
  directlySharedWithAccountId,
}: CampaignListProps) {
  // Pre-P2b-3 polish round 2 (Item 2): four filters + search.
  // Search input is debounced 200ms before participating in the
  // filter pipeline so typing stays responsive.
  //
  // Filter state is mirrored into URL search params so AMs can
  // bookmark / share filtered views (?q=...&status=active,inactive&...).
  // The mirror is one-way per direction:
  //   - Initial mount reads URL → state.
  //   - Subsequent state changes write state → URL (default values
  //     omitted to keep clean URLs).
  // We deliberately don't continuously sync URL → state on every
  // params change because that creates feedback loops with the write
  // direction. Back/forward browser navigation between filtered views
  // remounts via React Router's location change so the initial read
  // path covers it.
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = useMemo(
    () => readFiltersFromParams(searchParams),
    // Read once on mount — subsequent reads are blocked to avoid the
    // write-then-read feedback loop. eslint-disable correct here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [statusFilter, setStatusFilter] = useState<CampaignStatus[]>(
    initialFilters.status,
  );
  const [designationFilter, setDesignationFilter] = useState<DesignationFilter>(
    initialFilters.designation,
  );
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(
    initialFilters.scope,
  );
  const [brandFilter, setBrandFilter] = useState<string>(initialFilters.brand);
  const [searchInput, setSearchInput] = useState<string>(initialFilters.search);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialFilters.search);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchInput), 200);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // State → URL. Writes only non-default values; default state results
  // in a clean /campaigns URL. Uses the debounced search so we don't
  // thrash history on every keystroke. `replace: true` keeps the
  // browser's history stack from filling with one entry per filter
  // toggle.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    writeFiltersToParams(next, {
      search: debouncedSearch,
      status: statusFilter,
      designation: designationFilter,
      scope: scopeFilter,
      brand: brandFilter,
    });
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, designationFilter, scopeFilter, brandFilter]);

  // The two scoping modes resolve to one of three listCampaigns calls.
  // We pick the right query options up front rather than branching inside
  // useQuery so the queryKey stays stable per scope.
  const queryOpts = directlySharedWithAccountId
    ? {
        brandAccountId: directlySharedWithAccountId,
        filter: 'directlySharedWith' as const,
      }
    : brandAccountId
      ? { brandAccountId }
      : {};
  const queryScopeKey = directlySharedWithAccountId
    ? `direct:${directlySharedWithAccountId}`
    : brandAccountId
      ? `brand:${brandAccountId}`
      : 'global';

  const campaignsQuery = useQuery({
    queryKey: ['campaigns', queryScopeKey],
    queryFn: () => listCampaigns(queryOpts),
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
  });

  const brandById = useMemo(() => {
    const map = new Map<string, Account>();
    if (accountsQuery.data) {
      for (const a of accountsQuery.data) map.set(a.id, a);
    }
    return map;
  }, [accountsQuery.data]);

  const brandAccounts = useMemo<Account[]>(() => {
    if (!accountsQuery.data) return [];
    return accountsQuery.data.filter((a) => a.accountType === 'brand');
  }, [accountsQuery.data]);

  // Brand column is hidden when scoped to a single brand. In
  // directlySharedWith mode it stays visible because the surfaced
  // campaigns can originate from any brand whose shareTargets included
  // this account.
  const showBrandColumn = !brandAccountId;
  // Brand filter dropdown is only useful in fully global mode; both
  // scoped modes already constrain the list.
  const showBrandFilter = showBrandColumn && !directlySharedWithAccountId;
  // New campaign creation requires picking a brand. Show only in
  // global mode and brand-scoped mode (with brand pre-fill); hide in
  // directlySharedWith mode (non-brand AccountDetail).
  const showNewCampaignButton = !directlySharedWithAccountId;

  // Pre-P2b-3 polish round 2 (Item 2): combined filter pipeline.
  // Search runs against name + slug + description + every shareTarget's
  // resolved Account name + accountType label. Substring case-insensitive
  // match — typing "Cottesloe" matches both Cottesloe Group and any
  // location with Cottesloe in the name; typing "Brand" matches every
  // Campaign with a brand-typed share target.
  const filtered = useMemo(() => {
    const all = campaignsQuery.data ?? [];
    const statusSet = new Set(statusFilter);
    const q = debouncedSearch.trim().toLowerCase();
    return all.filter((c) => {
      // Status — empty selection means "show none" (matches the
      // "everything's been deselected" mental model).
      if (statusFilter.length > 0 && !statusSet.has(c.status)) return false;
      // Designation
      if (!matchesDesignation(c, designationFilter)) return false;
      // Brand (only enforced when the dropdown is showing)
      if (showBrandFilter && brandFilter && c.createdInBrandAccountId !== brandFilter) {
        return false;
      }
      // Scope
      if (!matchesScope(c, brandById, scopeFilter)) return false;
      // Search
      if (q) {
        const hay: string[] = [c.name.toLowerCase(), c.slug.toLowerCase()];
        if (c.description) hay.push(c.description.toLowerCase());
        for (const t of c.shareTargets ?? []) {
          const acc = brandById.get(t.accountId);
          if (acc) {
            hay.push(acc.name.toLowerCase());
            hay.push(ACCOUNT_TYPE_LABEL[acc.accountType].toLowerCase());
          }
        }
        if (!hay.some((s) => s.includes(q))) return false;
      }
      return true;
    });
  }, [
    campaignsQuery.data,
    statusFilter,
    designationFilter,
    scopeFilter,
    brandFilter,
    showBrandFilter,
    debouncedSearch,
    brandById,
  ]);

  // "Clear filters" reset for the empty-state hint. Resets to the default
  // selection (active + inactive on status) rather than the universal-all
  // shape so the user lands on the same view they had on first paint.
  const hasNonDefaultFilters =
    debouncedSearch.trim().length > 0 ||
    designationFilter !== 'all' ||
    scopeFilter !== 'all' ||
    brandFilter !== '' ||
    statusFilter.length !== DEFAULT_STATUS_SELECTION.length ||
    !DEFAULT_STATUS_SELECTION.every((s) => statusFilter.includes(s));
  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setStatusFilter(DEFAULT_STATUS_SELECTION);
    setDesignationFilter('all');
    setScopeFilter('all');
    setBrandFilter('');
  };

  if (campaignsQuery.isLoading || accountsQuery.isLoading) return <CenteredSpinner />;
  if (campaignsQuery.error) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load campaigns: {(campaignsQuery.error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          className="w-72"
          placeholder="Search name, description, or share target…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search campaigns"
        />
        <MultiSelectFilter
          label="Status"
          options={ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
          selected={statusFilter}
          onChange={(next) => setStatusFilter(next as CampaignStatus[])}
          debounceMs={0}
          placeholder="No statuses"
        />
        <SelectField
          aria-label="Designation"
          value={designationFilter}
          onValueChange={(v) => setDesignationFilter(v as DesignationFilter)}
          options={(Object.keys(DESIGNATION_LABEL) as DesignationFilter[]).map((d) => ({
            value: d,
            label: DESIGNATION_LABEL[d],
          }))}
        />
        <SelectField
          aria-label="Assigned scope"
          value={scopeFilter}
          onValueChange={(v) => setScopeFilter(v as ScopeFilter)}
          options={(Object.keys(SCOPE_LABEL) as ScopeFilter[]).map((s) => ({
            value: s,
            label: SCOPE_LABEL[s],
          }))}
        />
        {showBrandFilter && (
          <Combobox
            aria-label="Brand"
            searchPlaceholder="Search brands…"
            value={brandFilter}
            onValueChange={setBrandFilter}
            options={[
              { value: '', label: 'All brands' },
              ...brandAccounts.map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        )}
        {showNewCampaignButton && (
          <div className="ml-auto">
            <Link
              to={
                brandAccountId
                  ? `/campaigns/new?brand=${brandAccountId}`
                  : '/campaigns/new'
              }
            >
              <Button type="button">New campaign</Button>
            </Link>
          </div>
        )}
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              {showBrandColumn && <th className="text-left px-4 py-2">Brand</th>}
              <th className="text-left px-4 py-2">Description</th>
              <th className="text-left px-4 py-2">Assigned to</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Designation</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={showBrandColumn ? 6 : 5}
                  className="px-4 py-6 text-center text-slate-500"
                >
                  No campaigns match the current filters.{' '}
                  {hasNonDefaultFilters && (
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  )}
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const summary = summariseShareTargets(c, brandById);
              return (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/campaigns/${c.id}`} className="text-slate-900 hover:underline">
                      {c.name}
                    </Link>
                    <div className="text-xs text-slate-500 font-mono">{c.slug}</div>
                  </td>
                  {showBrandColumn && (
                    <td className="px-4 py-2 text-slate-700">
                      {brandById.get(c.createdInBrandAccountId)?.name ?? c.createdInBrandAccountId}
                    </td>
                  )}
                  <td className="px-4 py-2 text-slate-700 max-w-sm truncate">
                    {c.description ?? '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className="text-slate-700"
                      title={summary.full.length > 0 ? summary.full.join(' · ') : undefined}
                    >
                      {summary.headline}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={c.status} />
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        {channelsBadgeLabel(c.channels)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <DesignationBadge campaign={c} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
